"use client";
import { Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { useTheme } from "../theme/ThemeContext";

function PointerSvg({ width = "28", height = "50" }: { width?: string; height?: string }) {
  const theme = useTheme();
  const fill = theme.isDarkMode ? "#F1F1F1" : "#232629";
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height} viewBox="0 0 30 54" fill="none">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.5806 0.425247C14.3015 0.42748 14.0741 0.650007 14.0658 0.929024L12.5789 50.9447C12.5384 52.3092 13.6394 53.4345 15.0046 53.4235C16.3697 53.4126 17.4526 52.2699 17.3902 50.9062L15.1034 0.920723C15.0907 0.641875 14.8597 0.423014 14.5806 0.425247ZM16.0565 51.2898C16.1994 50.7563 15.8829 50.208 15.3494 50.0651C14.8159 49.9221 14.2676 50.2387 14.1247 50.7722C13.9817 51.3056 14.2983 51.854 14.8318 51.9969C15.3652 52.1399 15.9136 51.8233 16.0565 51.2898Z"
        fill={fill}
      />
    </svg>
  );
}

export interface InvestingGaugeProps {
  value?: number;
  min?: number;
  max?: number;
}

function getTrendKey(value: number) {
  if (value === 0) return undefined;
  if (value >= 0.5) return "Metrics.signal.trend.bullish";
  if (value >= 0) return "Metrics.signal.trend.slightlyBullish";
  if (value > -0.5) return "Metrics.signal.trend.slightlyBearish";
  return "Metrics.signal.trend.bearish";
}

function getTrendDescKey(value: number) {
  if (value === 0) return undefined;
  if (value >= 0.5) return "Metrics.signal.trend.highlyLikelyUp";
  if (value >= 0) return "Metrics.signal.trend.likelyUp";
  if (value > -0.5) return "Metrics.signal.trend.likelyDown";
  return "Metrics.signal.trend.highlyLikelyDown";
}

export default function InvestingGauge({ value = 0, min = -1, max = 1 }: InvestingGaugeProps) {
  const t = useTranslations();
  const startAngle = 190;
  const endAngle = -10;
  const targetAngle = getPointerAngle(value, max, min, startAngle, endAngle);
  const pointerWidth = "1.75rem";
  const pointerHeight = "3.125rem";

  // 动画：初始角度为最小值，加载后过渡到目标角度
  const [angle, setAngle] = useState(getPointerAngle(-1, max, min, startAngle, endAngle));

  useEffect(() => {
    const timer = setTimeout(() => setAngle(targetAngle), 50);
    return () => clearTimeout(timer);
  }, [targetAngle]);

  // 归一化到[-1,1]区间用于趋势判断
  const norm = ((value - min) / (max - min)) * 2 - 1;
  const trendKey = getTrendKey(norm);
  const trendDescKey = getTrendDescKey(norm);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
      <Box sx={{ width: "10rem", position: "relative", textAlign: "center" }}>
        <img
          src="/images/gauge-outer-ring.png"
          alt="gauge arc"
          style={{
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            left: `calc(50% - ${pointerWidth}/2)`,
            bottom: "0.75rem",
            transform: `rotate(${angle}deg)`,
            transformOrigin: "50% 98%",
            zIndex: 2,
            pointerEvents: "none",
            userSelect: "none",
            transition: "transform 0.7s cubic-bezier(0.4,0,0.2,1)",
          }}>
          <PointerSvg width={pointerWidth} height={pointerHeight} />
        </Box>
      </Box>
      <Typography
        variant="body1"
        sx={{
          fontWeight: 600,
          fontSize: "0.875rem",
          color: "text.primary",
          textAlign: "center",
          pt: "1rem",
        }}>
        {trendKey ? t(trendKey) : " "}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          fontSize: "var(--font-size-xs)",
          color: "text.secondary",
          textAlign: "center",
          pt: "0.75rem",
          maxWidth: "20rem",
        }}>
        {trendDescKey ? t(trendDescKey) : " "}
      </Typography>
    </Box>
  );
}

function getPointerAngle(value: number, max: number, min: number, startAngle: number, endAngle: number) {
  const percent = value / (max - min);
  const targetAngle = (startAngle - endAngle) * percent;
  return targetAngle;
}
